import { oc } from "@orpc/contract";
import { z } from "zod";
import { As400ExportGroupSchema } from "./products.contract";
import {
  AS400_PUBLISH_MODE_SEGMENT,
  As400PublishModeSchema,
  type As400PublishMode,
} from "./as400-publish.contract";

/**
 * Loading an AS/400 upload file back into a review — the inverse of the export.
 *
 * The app can turn a review into an upload file; this turns one back into staged
 * edits on a review that already exists. Three things want it: a batch Rexel
 * rejects has somewhere to go, a file someone else produced can be read against
 * live data before it loads, and a multi-worksheet change set can be built
 * outside the catalog's filters and brought in.
 *
 * Two entry points: {@link as400ImportContracts.preview}/`apply` stage a change
 * batch onto a review that already exists, and `previewNewReview`/`createReview`
 * start a new review from a batch — its change (`I2`) lines' products become the
 * review's membership with their cells staged, and its add (`I1`) lines become
 * the review's authored new rows.
 *
 * The file rules this reads — filenames, tab separation, no header, CRLF, blank
 * meaning "do not change" — are the publish rules read backwards, and they are
 * written down once, in `docs/inventory-management/as400-publishing.md` (§10 is
 * the import section). Read it before changing what a blank cell means.
 *
 * ## The two rules everything else follows from
 *
 * 1. **A blank cell is absent, never a clear.** A change (`I2`) upload reads a
 *    blank cell as "do not change", so an import that staged blanks would wipe
 *    every field the file didn't mention. A blank cell stages nothing at all.
 * 2. **A reset flag is the one blank that means something.** `RESET1..RESETn`
 *    carry `Y` beside a deliberately blank value column, and that is how the
 *    review itself spells a clear: the flag is a worksheet column with a staged
 *    value of its own, so it is imported as one. AS/400 does the clearing.
 */

/** The single-file cap. These are a few hundred KB of ASCII at worst. */
export const AS400_IMPORT_MAX_FILE_BYTES = 8 * 1024 * 1024;

/**
 * How many files one import takes. A run is at most one file per worksheet
 * ({@link AS400_PUBLISH_WORKSHEET_SEGMENT}, eight) plus its two location lists,
 * and a new review can take an adds run and a changes run together — so twenty,
 * and a few spare so a stray selection is refused by name rather than by arity.
 */
export const AS400_IMPORT_MAX_FILES = 24;

/**
 * The location-list files a drop can carry beside its worksheets, and the
 * worksheets each one fans out.
 *
 * `ITMSL` rides with an item file whose branch slot is `@`
 * (`AS400_PUBLISH_ALL_LOCATIONS_BRANCH`): Rexel's loader applies every other
 * file in the drop once per location it lists, one code per line. `STKSL` is
 * the stock files' equivalent, in the stocking code space (`as_stock.sloc`):
 * each STOCK / STKAD line whose LOC is blank applies once per location listed.
 */
export const AS400_IMPORT_LOCATION_LIST_SEGMENTS = ["ITMSL", "STKSL"] as const;
export type As400ImportLocationList =
  (typeof AS400_IMPORT_LOCATION_LIST_SEGMENTS)[number];

/** A location-list file's name read back: `I1_ITMSL_202610291237`. */
export function parseAs400LocationListFileName(fileName: string): {
  mode: As400PublishMode;
  list: As400ImportLocationList;
  timestamp: string;
} | null {
  const base = (fileName.split(/[\\/]/).pop() ?? "")
    .trim()
    .replace(/\.[A-Za-z0-9]+$/, "")
    .toUpperCase();
  const [modeSegment, listSegment, timestamp, ...rest] = base.split("_");
  if (rest.length > 0 || timestamp === undefined) return null;
  if (!/^\d{12}$/.test(timestamp)) return null;
  const mode = As400PublishModeSchema.options.find(
    (candidate) => AS400_PUBLISH_MODE_SEGMENT[candidate] === modeSegment,
  );
  const list = AS400_IMPORT_LOCATION_LIST_SEGMENTS.find(
    (candidate) => candidate === listSegment,
  );
  if (mode === undefined || list === undefined) return null;
  return { mode, list, timestamp };
}

/**
 * How many issues a response carries. A file whose layout we disagree about
 * produces one issue per line, and a thousand identical ones say nothing the
 * first twenty don't; the counts beside them stay exact.
 */
export const AS400_IMPORT_MAX_REPORTED_ISSUES = 200;

/**
 * How many row edits go into one staging call — the cap
 * `StageDataLinkEditsInputSchema` puts on its own input, mirrored here because a
 * real file exceeds it and the import batches rather than truncating.
 */
export const AS400_IMPORT_STAGE_BATCH_SIZE = 500;

/**
 * Why a line, a cell or a whole file was refused.
 *
 * Split into two kinds, because they are answered differently:
 *
 * **File-fatal** — the file is not what it says it is, so no line of it can be
 * trusted. Positional parsing means a single row of the wrong width makes every
 * cell after it a guess, and a duplicate record means two lines disagree about
 * one row. These are never skippable.
 *
 * **Line-level** — the file is well-formed and one line can't be placed. Those
 * are skippable, but only on an explicit say-so
 * ({@link As400ImportApplyInputSchema.shape.skipRejectedLines}), never by
 * default: a quietly dropped line is a change that was asked for and didn't
 * happen.
 */
export const As400ImportIssueCodeSchema = z.enum([
  // ── File-fatal ──────────────────────────────────────────────────────────────
  /** The filename names no worksheet or no run we recognise. */
  "UNREADABLE_FILE_NAME",
  /** The filename's run (`I1`/`I2`) is not the one the import was asked for. */
  "MODE_MISMATCH",
  /** The item file names a branch other than the one the import was asked for. */
  "BRANCH_MISMATCH",
  /** Two files in one import are for the same worksheet. */
  "DUPLICATE_WORKSHEET",
  /** The worksheet has no ingest mirror, so there is no record to key an edit against. */
  "WORKSHEET_HAS_NO_INGEST_TABLE",
  /** Not valid base64, or past {@link AS400_IMPORT_MAX_FILE_BYTES}. */
  "UNREADABLE_CONTENT",
  /** No data lines at all. */
  "EMPTY_FILE",
  /** A line's cell count is not the worksheet's column count. */
  "COLUMN_COUNT",
  /** A line carries no value in an identity column, so it names no record. */
  "MISSING_IDENTITY",
  /** Two lines name the same record. */
  "DUPLICATE_RECORD",
  /** A location list line is not one location code, or repeats one. */
  "INVALID_LOCATION_LIST",

  // ── Line-level ──────────────────────────────────────────────────────────────
  /** The item number matches no product in the catalog. */
  "UNKNOWN_ITEM_NUMBER",
  /** The product exists but is not in this review. */
  "NOT_IN_REVIEW",
  /** The review holds the product, but this division has no such record at this branch. */
  "NO_RECORD_IN_DIVISION",
  /** Several records match the line's identity columns, so it names more than one row. */
  "AMBIGUOUS_RECORD",
  /** A cell holds a character the AS/400 cannot take. */
  "NON_ASCII_CELL",
  /** A cell is longer than the column's stated maximum. */
  "CELL_TOO_LONG",
  /** A cell belongs to a column with no ingest row on this record to write to. */
  "UNATTRIBUTABLE_CELL",
  /** The same product is both added (`I1`) and changed (`I2`) in one batch. */
  "ADD_AND_CHANGE",
  /**
   * An added line can't be a new row at all: a column the worksheet doesn't
   * have, or an item number the workbook refuses.
   */
  "INVALID_NEW_ROW",
  /** Staging the file's edits failed part-way. */
  "STAGE_FAILED",

  // ── Warnings ────────────────────────────────────────────────────────────────
  /**
   * The line's values break a rule the review grid enforces when a cell is
   * typed. Loaded anyway: anything the app's own export can write, the import
   * reads back, and stored rows can predate a rule (or carry what the AS/400
   * itself stores — an ICAT5 whose fifth position is the serialization flag).
   * The grid flags the cell when it is next edited.
   */
  "CELL_RULE_WARNING",
]);
export type As400ImportIssueCode = z.infer<typeof As400ImportIssueCodeSchema>;

/** Issues reported but not refused: the line loads as written. */
export const AS400_IMPORT_WARNING_ISSUE_CODES: ReadonlySet<As400ImportIssueCode> =
  new Set<As400ImportIssueCode>(["CELL_RULE_WARNING"]);

/** Whether an issue is a warning — the line still loads. */
export function as400ImportIssueIsWarning(code: As400ImportIssueCode): boolean {
  return AS400_IMPORT_WARNING_ISSUE_CODES.has(code);
}

/**
 * The issue codes no `skipRejectedLines` can get past — the file is not the file
 * it claims to be. Declared here rather than at the check sites so a client can
 * tell a "fix the file" problem from a "skip these lines" one without a second
 * round-trip.
 */
export const AS400_IMPORT_FATAL_ISSUE_CODES: ReadonlySet<As400ImportIssueCode> =
  new Set<As400ImportIssueCode>([
    "UNREADABLE_FILE_NAME",
    "MODE_MISMATCH",
    "BRANCH_MISMATCH",
    "DUPLICATE_WORKSHEET",
    "WORKSHEET_HAS_NO_INGEST_TABLE",
    "UNREADABLE_CONTENT",
    "EMPTY_FILE",
    "COLUMN_COUNT",
    "MISSING_IDENTITY",
    "DUPLICATE_RECORD",
    "INVALID_LOCATION_LIST",
    "STAGE_FAILED",
  ]);

/** Whether an issue refuses the whole file rather than one of its lines. */
export function as400ImportIssueIsFatal(code: As400ImportIssueCode): boolean {
  return AS400_IMPORT_FATAL_ISSUE_CODES.has(code);
}

/**
 * One thing wrong, named the way a person reading a rejection needs it: which
 * file, which line, which column, and why.
 */
export const As400ImportIssueSchema = z.object({
  /** The file as the caller named it, echoed verbatim. */
  fileName: z.string(),
  /**
   * 1-based line number within the file, counting data lines as the file counts
   * them (there is no header row). Null for a problem with the file as a whole.
   */
  line: z.number().int().positive().nullable(),
  /**
   * The worksheet column, by its edit key (field code, or an export-only
   * column's key). Null when the issue isn't about one column.
   */
  column: z.string().nullable(),
  code: As400ImportIssueCodeSchema,
  /** One sentence naming the values involved, ready to show. */
  detail: z.string(),
});
export type As400ImportIssue = z.infer<typeof As400ImportIssueSchema>;

/** What one file in the import would do, or did. */
export const As400ImportFileReportSchema = z.object({
  fileName: z.string(),
  /** The worksheet its name names; null when the name couldn't be read. */
  group: As400ExportGroupSchema.nullable(),
  /** The run its name names; null when the name couldn't be read. */
  mode: As400PublishModeSchema.nullable(),
  /** The branch its name names — the item file only; null otherwise. */
  branch: z.string().nullable(),
  /** Data lines in the file. */
  lineCount: z.number().int().nonnegative(),
  /** Lines that resolved to exactly one record in the review. */
  resolvedLineCount: z.number().int().nonnegative(),
  /**
   * Resolved lines that ask for no change at all — every non-identity cell
   * blank, or already holding the value the file carries.
   *
   * Not a problem: it is most of a round-tripped batch. The item file names
   * every product the batch touches, and a product the review changed nothing
   * about appears in it as its number and blanks.
   */
  noChangeLineCount: z.number().int().nonnegative(),
  /** Records the file would change (or changed). */
  changedRowCount: z.number().int().nonnegative(),
  /** Cells across those records. */
  changedCellCount: z.number().int().nonnegative(),
  /**
   * Authored new rows an adds (`I1`) file would create — one per line per
   * location it is added at. Zero for a change file.
   */
  newRowCount: z.number().int().nonnegative(),
  /** Lines refused. Each has at least one issue naming it. */
  rejectedLineCount: z.number().int().nonnegative(),
  /** Whether this file was staged. Always false on a preview. */
  applied: z.boolean(),
  issues: z.array(As400ImportIssueSchema),
});
export type As400ImportFileReport = z.infer<typeof As400ImportFileReportSchema>;

/** The files of one import, each with its name and its bytes. */
export const As400ImportFilesSchema = z
  .array(
    z.object({
      /** The file's name, which is what says which worksheet and run it is. */
      fileName: z.string().trim().min(1).max(255),
      /**
       * The file's bytes, base64-encoded.
       *
       * Bytes rather than text on purpose: the AS/400 accepts printable ASCII
       * and nothing else, and a file carrying EBCDIC-conversion noise has to
       * fail naming the offending cell. Decoding in the browser would have
       * already replaced those bytes with `U+FFFD` and lost which byte it was.
       */
      contentBase64: z.string().min(1),
    }),
  )
  .min(1)
  .max(AS400_IMPORT_MAX_FILES);

/**
 * What the import covers: one review, one division, one branch, one run.
 *
 * The same unit a publish takes ({@link As400PublishInputSchema}), and for the
 * same reason — a batch is one branch's drop. Only the item file's *name* carries
 * the branch, so a price file on its own says nothing about which of a division's
 * locations its rows belong to; asking for the branch is what keeps a `PRICE`
 * row from being matched against seven branches' records at once.
 *
 * `mode` and `branch` are the caller's declaration, not the filename's. Each
 * file's own name is parsed and must agree — the filename is user-supplied, and
 * a blank cell means opposite things in the two runs, so an `I1` file loaded as
 * a change (or the reverse) is refused rather than reconciled.
 */
export const As400ImportInputSchema = z.object({
  changeSetId: z.uuid(),
  /** The division (DataSource) whose records the files name. */
  dataSourceId: z.uuid(),
  /**
   * Which run the files are for. `CHANGE` only, for now: an `ADD` file authors
   * net-new rows, which is a different write channel
   * (`MetadataChangeSetNewRowsService`) and needs the division-and-branch
   * authoring semantics a review's Add-row control has. An `I1` file is refused
   * by name rather than half-imported.
   */
  mode: As400PublishModeSchema,
  /**
   * The branch every file's rows load into — the item file's third segment.
   * `@` reads the locations from the batch's `ITMSL` file instead, and every
   * line is matched once per location listed, as Rexel's loader applies it.
   * Absent takes the item file's own; a batch of stock files alone needs none,
   * since they are keyed by their LOC or the batch's `STKSL`.
   */
  branch: z
    .string()
    .trim()
    .toUpperCase()
    .regex(
      /^[0-9A-Z@]$/,
      "A branch is one alphanumeric character, or @ with an ITMSL file",
    )
    .optional(),
  /**
   * Bring the catalog products the files name that the review doesn't hold
   * into it, then stage their lines — instead of refusing those lines as
   * `NOT_IN_REVIEW`. Never inferred: the preview lists them
   * ({@link As400ImportPlanResponseSchema.shape.productsNotInReview}) and the
   * reader says yes. An item number the catalog doesn't have is refused either
   * way.
   */
  addMissingProducts: z.boolean().default(false),
  files: As400ImportFilesSchema,
});
export type As400ImportInput = z.infer<typeof As400ImportInputSchema>;

export const As400ImportPlanResponseSchema = z.object({
  /**
   * Item numbers the files name that are in the catalog but not in the review
   * — what `addMissingProducts` would bring in. Empty once they are, and for a
   * new review, which holds whatever its files name.
   */
  productsNotInReview: z.array(z.string()),
  files: z.array(As400ImportFileReportSchema),
  /** Records the import would change, across every file. */
  changedRowCount: z.number().int().nonnegative(),
  /** Cells across those records. */
  changedCellCount: z.number().int().nonnegative(),
  /** Lines refused, across every file. */
  rejectedLineCount: z.number().int().nonnegative(),
  /**
   * True when a file was refused outright — the file is not what its name says,
   * and no `skipRejectedLines` will get it in. The fix is to the file.
   */
  hasFatalIssue: z.boolean(),
  /**
   * Whether `apply` would go through as asked, with no `skipRejectedLines`.
   * False when anything at all was refused.
   */
  canApply: z.boolean(),
});
export type As400ImportPlanResponse = z.infer<
  typeof As400ImportPlanResponseSchema
>;

export const As400ImportApplyInputSchema = As400ImportInputSchema.extend({
  /**
   * Stage the lines that resolved and leave the refused ones out, instead of
   * refusing the import.
   *
   * Off by default and never inferred: a line that couldn't be placed is a
   * change someone asked for, and dropping it silently is the failure this whole
   * endpoint exists to avoid. A caller sets it after showing the rejections —
   * which is exactly what the dialog does with the preview. It cannot get past a
   * file-fatal issue ({@link AS400_IMPORT_FATAL_ISSUE_CODES}).
   */
  skipRejectedLines: z.boolean().default(false),
});
export type As400ImportApplyInput = z.infer<typeof As400ImportApplyInputSchema>;

export const As400ImportApplyResponseSchema =
  As400ImportPlanResponseSchema.omit({ canApply: true }).extend({
    /**
     * Ingest rows whose staged change was written or removed — what
     * `stageDataLinkEdits` counts. Not the same as `changedRowCount`: a Purchase
     * Data worksheet row spreads across two ingest rows, and a row whose every
     * edit reverted to live is removed rather than written.
     */
    savedRowCount: z.number().int().nonnegative(),
    /** Products brought into the review (`addMissingProducts`). */
    addedProductCount: z.number().int().nonnegative(),
  });
export type As400ImportApplyResponse = z.infer<
  typeof As400ImportApplyResponseSchema
>;

/**
 * Start a new review from a batch of upload files.
 *
 * The batch says everything a publish would: each run's item file names its
 * branch (`@` with an `ITMSL` file for several locations), and the run is the
 * filename's `I1`/`I2`. Both runs can arrive together, as separate files:
 *
 * - **Change (`I2`) lines** name products the catalog already has. Each such
 *   product joins the review, and its cells are staged exactly as an import
 *   into an existing review stages them. A line whose product or record does
 *   not exist is refused — a change cannot apply to a missing record.
 * - **Add (`I1`) lines** become the review's authored new rows, one per line
 *   per location, with the line's values. In an adds file a blank cell is a
 *   blank value, so it is simply not set.
 *
 * The same product added and changed in one batch is refused line by line:
 * which of the two the AS/400 should see first is not ours to decide.
 */
export const As400ImportNewReviewInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).optional(),
  /** The division (DataSource) the batch is for. */
  dataSourceId: z.uuid(),
  files: As400ImportFilesSchema,
});
export type As400ImportNewReviewInput = z.infer<
  typeof As400ImportNewReviewInputSchema
>;

/** One product the batch adds, with where its rows land. */
export const As400ImportNewItemSchema = z.object({
  itemNumber: z.string(),
  /** The worksheets it gets a row on, in workbook order. */
  groups: z.array(As400ExportGroupSchema),
  /**
   * The locations it is added at. Null for a division whose adds are not split
   * by location — the row is division-wide, as the review's own Add row
   * authors it there.
   */
  branches: z.array(z.string()).nullable(),
});
export type As400ImportNewItem = z.infer<typeof As400ImportNewItemSchema>;

/** One run of the batch: its mode and the locations it applies at. */
export const As400ImportRunSchema = z.object({
  mode: As400PublishModeSchema,
  /** From the item file's name, or from `ITMSL` when that names `@`. */
  branches: z.array(z.string()),
});
export type As400ImportRun = z.infer<typeof As400ImportRunSchema>;

export const As400ImportNewReviewPlanResponseSchema =
  As400ImportPlanResponseSchema.extend({
    runs: z.array(As400ImportRunSchema),
    /** Existing products the review would hold. */
    productCount: z.number().int().nonnegative(),
    /** Products the batch adds. */
    newItemCount: z.number().int().nonnegative(),
    /** The first {@link AS400_IMPORT_MAX_REPORTED_ISSUES} of them. */
    newItems: z.array(As400ImportNewItemSchema),
    /** Authored rows across every worksheet and location. */
    newRowCount: z.number().int().nonnegative(),
  });
export type As400ImportNewReviewPlanResponse = z.infer<
  typeof As400ImportNewReviewPlanResponseSchema
>;

export const As400ImportCreateReviewInputSchema =
  As400ImportNewReviewInputSchema.extend({
    /** See {@link As400ImportApplyInputSchema}. */
    skipRejectedLines: z.boolean().default(false),
  });
export type As400ImportCreateReviewInput = z.infer<
  typeof As400ImportCreateReviewInputSchema
>;

export const As400ImportCreateReviewResponseSchema =
  As400ImportNewReviewPlanResponseSchema.omit({ canApply: true }).extend({
    changeSetId: z.uuid(),
    /** Ingest rows whose staged change was written. */
    savedRowCount: z.number().int().nonnegative(),
  });
export type As400ImportCreateReviewResponse = z.infer<
  typeof As400ImportCreateReviewResponseSchema
>;

export const as400ImportContracts = {
  preview: oc
    .input(As400ImportInputSchema)
    .output(As400ImportPlanResponseSchema)
    .route({
      method: "POST",
      summary: "Preview loading ERP upload files into a review",
      description:
        "Reads ERP (AS/400) upload files against an existing review without staging anything: what each line would change, and every line refused with its reason.",
    }),
  apply: oc
    .input(As400ImportApplyInputSchema)
    .output(As400ImportApplyResponseSchema)
    .route({
      method: "POST",
      summary: "Load ERP upload files into a review",
      description:
        "Stages the cells of ERP (AS/400) change upload files as edits on an existing review. Refused lines are skipped only when asked.",
    }),
  previewNewReview: oc
    .input(As400ImportNewReviewInputSchema)
    .output(As400ImportNewReviewPlanResponseSchema)
    .route({
      method: "POST",
      summary: "Preview a new review from ERP upload files",
      description:
        "Reads a batch of ERP (AS/400) upload files as a new review without creating it: the existing products it would hold, the new items it would add at each location, and every line refused or warned about.",
    }),
  createReview: oc
    .input(As400ImportCreateReviewInputSchema)
    .output(As400ImportCreateReviewResponseSchema)
    .route({
      method: "POST",
      summary: "Create a review from ERP upload files",
      description:
        "Creates a review from a batch of ERP (AS/400) upload files in one transaction: change lines' products become its members with their cells staged, and add lines become its new rows.",
    }),
} as const;
